import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Target, Brain, Settings } from "lucide-react";
import BasicAdvancedWrapper, {
  BasicAdvancedWrapperProps,
} from "./components/BasicAdvancedWrapper";
import { MultiStepConfig } from "./components/MultiStepGradeModal";
import { CreateModalProps } from "./index";
import {
  UsagePurposeStep,
  TargetSkillsStep,
  ClassroomImplementationStep,
} from "./real-world-connections/steps";
import RealWorldConnectionSpecificFields from "./real-world-connections/RealWorldConnectionSpecificFields";
import {
  RealWorldConnectionCreateRequestDto,
  RealWorldConnectionCreateRequestDtoContentLanguage,
  RealWorldConnectionCreateRequestDtoCourseName,
  RealWorldConnectionCreateRequestDtoGenerationType,
} from "@packages/api/madlenAPI.schemas";

interface RealWorldConnectionCreateModalProps
  extends Omit<CreateModalProps, "onSubmit" | "themeColor"> {
  onSubmit: (input: any) => Promise<void>;
  themeColor?: string;
}

export const RealWorldConnectionCreateModal: React.FC<
  RealWorldConnectionCreateModalProps
> = ({ onSubmit, initialInput, ...props }) => {
  const { t } = useTranslation();

  // State for basic context field
  const [basicContext, setBasicContext] = useState(
    (initialInput as any).context || "",
  );

  // Define the initial data structure for advanced mode
  const initialAdvancedData: RealWorldConnectionCreateRequestDto = {
    // Basic CreateModal data (will be populated from first step)
    courseName:
      initialInput.courseName as RealWorldConnectionCreateRequestDtoCourseName,
    gradeLevel: initialInput.gradeLevel,
    prompt: initialInput.prompt,
    environment: initialInput.environment,
    isPublic: initialInput.isPublic,
    generationType:
      initialInput.generationType as RealWorldConnectionCreateRequestDtoGenerationType,
    teacherDocumentIds: initialInput.teacherDocumentIds || [],
    learningOutcomeIds: initialInput.learningOutcomeIds || [],
    contentLanguage:
      initialInput.outputLanguage as RealWorldConnectionCreateRequestDtoContentLanguage,

    // Advanced real-world connection specific defaults
    usagePurposes: [],
    targetSkills: [],
    classroomImplementation: "",
  };

  // Define the advanced steps configuration
  const advancedSteps: MultiStepConfig<RealWorldConnectionCreateRequestDto>[] =
    [
      {
        id: "usage-purpose",
        title: t("realWorld.steps.purpose.title"),
        description: t("realWorld.steps.purpose.description"),
        icon: <Target size={16} />,
        component: UsagePurposeStep,
        validation: (data) => {
          // At least one usage purpose should be selected
          return !!(data.usagePurposes && data.usagePurposes.length > 0);
        },
      },
      {
        id: "target-skills",
        title: t("realWorld.steps.skills.title"),
        description: t("realWorld.steps.skills.description"),
        icon: <Brain size={16} />,
        component: TargetSkillsStep,
        validation: (data) => {
          // This step is optional, so always valid
          return true;
        },
      },
      {
        id: "classroom-implementation",
        title: t("realWorld.steps.implementation.title"),
        description: t("realWorld.steps.implementation.description"),
        icon: <Settings size={16} />,
        component: ClassroomImplementationStep,
        validation: (data) => {
          // This step is optional, so always valid
          return true;
        },
      },
    ];

  // Handle basic mode submission (original behavior)
  const handleBasicSubmit = async (data: any) => {
    let submissionData = { ...data };

    // Combine context with prompt if context is provided
    if (basicContext.trim()) {
      submissionData.prompt = `${basicContext.trim()}\n\n${data.prompt}`;
    }

    await onSubmit(submissionData);
  };

  // Handle advanced mode submission
  const handleAdvancedSubmit = async (
    data: RealWorldConnectionCreateRequestDto,
  ) => {
    let submissionData = { ...data };

    // Construct enriched prompt based on advanced settings
    let enrichedPrompt = data.prompt || "";

    // Add context if provided from basic context state
    if (basicContext?.trim()) {
      enrichedPrompt = `${basicContext.trim()}\n\n${enrichedPrompt}`;
    }

    // Add usage purposes
    if (data.usagePurposes && data.usagePurposes.length > 0) {
      enrichedPrompt += `\n\nUsage Purposes: ${data.usagePurposes.join(", ")}`;
    }

    // Add target skills
    if (data.targetSkills && data.targetSkills.length > 0) {
      enrichedPrompt += `\nTarget Skills: ${data.targetSkills.join(", ")}`;
    }

    // Add classroom implementation scenarios
    if (data.classroomImplementation && data.classroomImplementation.trim()) {
      enrichedPrompt += `\nClassroom Implementation: ${data.classroomImplementation}`;
    }

    submissionData.prompt = enrichedPrompt;

    await onSubmit(submissionData);
  };

  // Handle context change from basic mode
  const handleContextChange = (context: string) => {
    setBasicContext(context);
  };

  // Prepare the wrapper props
  const wrapperProps: BasicAdvancedWrapperProps<RealWorldConnectionCreateRequestDto> =
    {
      createModalProps: {
        ...props,
        initialInput,
        themeColor: "orange",
        onSubmit: handleBasicSubmit,
        after: (
          <RealWorldConnectionSpecificFields
            context={basicContext}
            onChange={handleContextChange}
            themeColor="orange"
          />
        ),
      },
      multiStepProps: {
        steps: advancedSteps,
        initialData: initialAdvancedData,
        title: t("quickActions.realWorldConnectionCreate"),
        themeColor: "orange",
        backPath: "/content/connections",
        isLoading: props.isLoading,
        error: props.error,
        loadingTitle: props.loadingTitle,
        errorTitle: props.errorTitle,
        errorDescription: props.errorDescription,
        onFinalSubmit: handleAdvancedSubmit,
        basicModeSubmitText: t("common.generate"),
        advancedModeSubmitText: t("realWorld.createAdvanced"),
      },
      themeColor: "orange",
      title: t("quickActions.realWorldConnectionCreate"),
      backPath: "/content/connections",
      enableAdvancedMode: true,
      defaultMode: "basic",
      showModeToggle: true,
      onBasicSubmit: handleBasicSubmit,
      onAdvancedSubmit: handleAdvancedSubmit,
    };

  return <BasicAdvancedWrapper {...wrapperProps} />;
};

export default RealWorldConnectionCreateModal;
